import history from '@/settings/history';
import { HttpClient } from '@/services/api/types';
import { LOGIN_URL, UNAUTHORIZED } from './constants';
import { createHTTPRequest } from './createHTTPRequest';
import { lookupExactTotal, scheduleExactTotal } from './deferredListTotal';
import { handleResponse } from './handleResponse';
import { DataProviderPayload, DataProviderResource, FetchType, HandleResponse } from './types';
import { getTotal } from './utils';

const DEFERRED_LIST_RESOURCES = new Set<DataProviderResource>(['candidates', 'adverse']);

const withDeferredCount = (type: FetchType, resource: DataProviderResource, payload: DataProviderPayload) => {
  if (type !== 'GET_LIST' || !DEFERRED_LIST_RESOURCES.has(resource) || payload?.meta?.countOnly) {
    return payload;
  }
  return { ...payload, meta: { ...payload?.meta, deferCount: true } };
};

const isAbort = (err: unknown) => (err as { name?: string })?.name === 'AbortError';

const statusOf = (err: unknown) => (err as { status?: number })?.status;

const withoutProvisionalTotal = (payload: DataProviderPayload, result: HandleResponse): HandleResponse => {
  const page = payload.pagination?.page ?? 1;
  return {
    data: result.data,
    pageInfo: { hasNextPage: true, hasPreviousPage: page > 1 },
    meta: { countDeferred: true },
  };
};

/**
 * Query a data provider and return a promise for a response
 *
 * @example
 * dataProvider(GET_ONE, 'posts', { id: 123 })
 *  => Promise.resolve({ data: { id: 123, title: "hello, world" } })
 *
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} payload Request parameters. Depends on the action type
 * @returns {Promise} the Promise for a response
 */
export const turnAPI =
  (httpClient: HttpClient) => async (type: FetchType, resource: DataProviderResource, payload: DataProviderPayload) => {
    try {
      const requestPayload = withDeferredCount(type, resource, payload);
      const { url, options } = createHTTPRequest(type, resource, requestPayload);
      const res = await httpClient(url, options);
      const result = handleResponse(res, type, requestPayload);

      if (result.meta?.countDeferred) {
        const known = lookupExactTotal(resource, payload);
        scheduleExactTotal(resource, payload, (signal) => loadExactTotal(httpClient, type, resource, payload, signal));
        if (known != null) return { ...result, total: known };
        return withoutProvisionalTotal(payload, result);
      }

      return result;
    } catch (err: any) {
      if (err?.status === UNAUTHORIZED) {
        window.location.replace(LOGIN_URL);
      }

      console.error('Error in turnAPI:', err);
      throw err; // react-admin will handle the error
    }
  };

const loadExactTotal = async (
  httpClient: HttpClient,
  type: FetchType,
  resource: DataProviderResource,
  payload: DataProviderPayload,
  signal: AbortSignal
) => {
  const attempt = async () => {
    const countPayload = { ...payload, meta: { countOnly: true }, signal };
    const countRequest = createHTTPRequest(type, resource, countPayload);
    const countRes = await httpClient(countRequest.url, countRequest.options);
    return getTotal(countRes.headers);
  };

  try {
    return await attempt();
  } catch (err) {
    if (isAbort(err)) throw err;
    if (statusOf(err) === UNAUTHORIZED) {
      window.location.replace(LOGIN_URL);
      throw err;
    }
    try {
      return await attempt();
    } catch (retryErr) {
      if (isAbort(retryErr)) throw retryErr;
      if (statusOf(retryErr) === UNAUTHORIZED) {
        window.location.replace(LOGIN_URL);
        throw retryErr;
      }
      console.error('FCRA list count failed', retryErr);
      throw retryErr;
    }
  }
};
